/**
 * Scoped Capability Resolver - Canonical Authorization Engine
 * 
 * Determines user access based on:
 * - Organization account_role (canonical org roles: owner, admin, member)
 * - Active organization/workspace membership (scoped relationship)
 * - Access level (view, edit, manage, admin within scope)
 * - Explicit right (one-time grant, delegation, time-limited)
 * - Lifecycle state (suspended, expired, revoked, active)
 * - AAL/JIT conditions (multi-factor auth level, time-based, resource-exact)
 * 
 * STRICT AUTHORITY RULES:
 * - workspace_role: Evidence/compatibility ONLY - never authorizes directly
 * - platform_role: Control-plane operations ONLY - does NOT provide customer-data access
 * - account_role: Canonical org role (owner, admin, member) + customer membership
 * - Customer-data for support: Exact-resource, time-limited, read-only JIT only
 * 
 * This is the CANONICAL resolver - all other permission checks must flow through here.
 * Never interpret legacy role strings, workspace_role, or permission maps directly.
 */

export type Capability =
  | 'project.view'
  | 'project.edit'
  | 'project.manage'
  | 'project.participant.view'
  | 'project.participant.manage'
  | 'project.delete'
  | 'workspace.view'
  | 'workspace.manage'
  | 'workspace.members.invite'
  | 'workspace.members.manage'
  // Stage-1 workspace SURFACE discovery (Model A). Entering a list surface is
  // NOT access to the records on it — rows are filtered by resource-scoped
  // canResolve (project.view / document.view / investment.view / task scope).
  | 'workspace.projects.list'
  | 'workspace.tasks.list'
  | 'workspace.files.list'
  | 'workspace.analytics.view'
  | 'investment.view'
  | 'investment.approve'
  | 'investment.manage'
  | 'milestone.view'
  | 'milestone.edit'
  | 'milestone.gate.approve'
  | 'document.view'
  | 'document.preview'
  | 'document.download'
  | 'document.upload'
  | 'document.update'
  | 'document.delete'
  | 'document.approve'
  | 'document.sign'
  | 'document.share'
  | 'document.restricted_download'
  | 'document.internal_details.view'
  // Vault / data-room access capabilities (separate from ordinary document access).
  | 'vault.view'
  | 'vault.manage'
  | 'vault.contents.manage'
  | 'vault.security.manage'
  | 'vault.documents.link'
  | 'vault.documents.unlink'
  // Folder management (project-scoped).
  | 'folder.create'
  | 'folder.manage'
  // Task-scoped capabilities. Authority flows through the task's parent project
  // (any project relationship grants view; project_manager grants manage/assign/delete;
  // assignee/creator narrows to own-task self-service only).
  | 'task.view'
  | 'task.create'
  | 'task.update'
  | 'task.status.update'
  | 'task.assign'
  | 'task.delete'
  | 'task.approve'
  | 'task.comment'
  | 'task.attachment.view'
  | 'task.attachment.download'
  | 'task.internal_details.view'
  | 'task.participants.manage'
  | 'users.view'
  | 'users.manage'
  | 'system.settings'
  | 'system.roles'
  | 'system.security'
  | 'audit.view';

export interface ScopedContext {
  workspaceId?: string;
  projectId?: string;
  investmentId?: string;
  companyId?: string;
  milestoneId?: string;
  documentId?: string;
  organizationId?: string;
  /** The specific task being evaluated (for task.* capabilities). */
  taskId?: string;
  /**
   * Id of the user the task is currently assigned to. Used for narrow own-task
   * self-service checks inside task.* capability resolution. Never use this to
   * derive project authority — assignment is not a project relationship.
   */
  taskAssigneeId?: string;
  /** Id of the user who created the task (for own-task delete/view self-service). */
  taskCreatorId?: string;
  /** Id of the vault/data-room being evaluated (for vault.* capabilities). */
  vaultId?: string;
  /** Id of the user who uploaded/owns the document (for own-doc self-service). */
  documentOwnerId?: string;
}

export interface UserAuthState {
  userId: string | null | undefined;
  
  /**
   * Canonical organization account role: owner, admin, member
   * This is the ONLY role that authorizes customer-data access (plus scoped relationships)
   * DO NOT use workspace_role or platform_role for customer-data authorization.
   */
  accountRole?: string | null;
  
  /**
   * Platform role for control-plane operations only (system.settings, audit.view, etc.)
   * Platform admins do NOT automatically access customer workspaces, projects, or investments.
   * Customer-data access for support must be exact-resource, time-limited, read-only via JIT.
   */
  platformRole?: string | null;
  
  /**
   * workspace_role is evidence/compatibility ONLY
   * NEVER use this field for authorization decisions.
   * It is retained only for backward compatibility and audit trails.
   */
  workspaceRole?: string | null;
  
  /**
   * Scoped relationships - map of relationship_type -> scope reference
   * e.g. { 'project_manager': 'project-123', 'investor': 'investment-456' }
   * These grant access within specific scopes (workspace, project, investment, etc.)
   *
   * @deprecated single-value legacy shape. Prefer `scopedRelationshipFacts`,
   * which carries the canonical multi-resource facts produced by
   * `scopedRelationshipResolver.buildScopedRelationshipFacts()`.
   */
  relationships?: Record<string, string>;

  /**
   * Canonical scoped-relationship facts fed from
   * `scopedRelationshipResolver.buildScopedRelationshipFacts()`.
   * Keyed `${relationshipType}:${resourceType}` -> resource ids that have an
   * active, at-least-`view` relationship. This is the single hand-off point
   * that keeps ONE authority engine: the relationship resolver produces facts,
   * this resolver makes the final decision.
   */
  scopedRelationshipFacts?: Record<string, string[]>;
  
  /** Explicit rights granted via delegation or one-time grants */
  explicitRights?: Array<{
    capability: Capability;
    context: ScopedContext;
    expiresAt?: Date;
  }>;
  
  /** AAL (Authentication Assurance Level) - tracks MFA status */
  aal?: 'aal1' | 'aal2' | 'aal3';
  
  /** User suspension state */
  suspended?: boolean;
  suspendedAt?: Date;
}

/**
 * Core resolution function - determines if user can perform capability in context
 * 
 * Fail-closed: returns false if any required condition is missing
 * CRITICAL: workspace_role is NEVER used for authorization - only accountRole + scoped relationships
 */
export function canResolve(
  user: UserAuthState | null | undefined,
  capability: Capability,
  context?: ScopedContext,
): boolean {
  // Fail closed: no user = no access
  if (!user?.userId) return false;

  // Fail closed: suspended user = no access
  if (user.suspended) return false;

  // Check explicit rights first (highest priority - explicit grant)
  if (user.explicitRights?.some(right =>
    right.capability === capability &&
    contextMatches(right.context, context) &&
    (!right.expiresAt || right.expiresAt > new Date())
  )) {
    return true;
  }

  // Check capability against account role + scoped relationships
  return evaluateScopedCapability(user, capability, context);
}

/**
 * Canonical organization administration roles. These are the ONLY account
 * roles that confer organization-wide (non-scoped) authority. Legacy admin
 * aliases such as `workspace_owner`, `business_admin` and `workspace_admin`
 * are NOT accepted here.
 */
function isOrganizationAdminRole(accountRole: string | null | undefined): boolean {
  return accountRole === 'organization_owner' || accountRole === 'organization_admin';
}

/**
 * Consult the canonical scoped-relationship facts fed from
 * `scopedRelationshipResolver.buildScopedRelationshipFacts()`. Returns true
 * only when the user holds an active relationship of one of `types` (or any
 * type when omitted) scoped to the exact requested resource. Fails closed on
 * missing facts/scope.
 */
function hasRelationshipForResource(
  user: UserAuthState,
  resourceType: string,
  resourceId: string | undefined,
  types?: string[],
): boolean {
  if (!resourceId) return false;
  const facts = user.scopedRelationshipFacts;
  if (!facts) return false;
  for (const [key, ids] of Object.entries(facts)) {
    const separatorIndex = key.lastIndexOf(':');
    if (separatorIndex < 0) continue;
    const relationshipType = key.slice(0, separatorIndex);
    const factResourceType = key.slice(separatorIndex + 1);
    if (factResourceType !== resourceType) continue;
    if (types && !types.includes(relationshipType)) continue;
    if (ids.includes(resourceId)) return true;
  }
  return false;
}

/**
 * Determine if two contexts overlap (for scope matching)
 */
function contextMatches(grantedContext: ScopedContext, requestedContext?: ScopedContext): boolean {
  if (!requestedContext) {
    // If no specific context requested, check if grant is workspace-wide
    return !grantedContext.projectId &&
           !grantedContext.investmentId &&
           !grantedContext.milestoneId;
  }

  // Check if requested context falls within granted scope
  if (grantedContext.projectId && grantedContext.projectId !== requestedContext.projectId) {
    return false;
  }
  if (grantedContext.investmentId && grantedContext.investmentId !== requestedContext.investmentId) {
    return false;
  }
  if (grantedContext.workspaceId && grantedContext.workspaceId !== requestedContext.workspaceId) {
    return false;
  }

  return true;
}

/**
 * Evaluate scoped capability based on account role + scoped relationships
 * 
 * AUTHORITY MODEL:
 * - Control-plane operations: platformRole only (system.settings, audit.view, etc.)
 * - Customer-data access: accountRole (canonical org role) + scoped relationships only
 * - workspace_role: NEVER used for authorization (compatibility evidence only)
 * 
 * Fail-closed: returns false if scope is missing/ambiguous or relationships don't match
 */
function evaluateScopedCapability(
  user: UserAuthState,
  capability: Capability,
  context?: ScopedContext,
): boolean {
  // Control-plane operations require a CANONICAL platform account role.
  // Legacy granular platform labels (system_admin, security_admin, …) and the
  // evidence-only `platformRole` field never authorize. Platform FUNCTIONS are
  // non-authorizing metadata and are intentionally not consulted here.
  if (capability === 'system.settings' || capability === 'system.roles' || capability === 'system.security') {
    return user.accountRole === 'platform_admin' || user.accountRole === 'platform_owner';
  }
  if (capability === 'audit.view') {
    return user.accountRole === 'platform_admin' || user.accountRole === 'platform_owner';
  }

  // CRITICAL: Platform role NEVER grants customer-data access
  // All customer-data operations require organization membership + accountRole + scoped relationships

  // Project-scoped capabilities (require projectId in context)
  if (capability.startsWith('project.')) {
    if (!context?.projectId) return false; // Fail closed: no project context
    return evaluateProjectCapability(user, capability, context);
  }

  // Workspace-scoped capabilities (require workspaceId in context)
  if (capability.startsWith('workspace.')) {
    if (!context?.workspaceId) return false;
    return evaluateWorkspaceCapability(user, capability, context);
  }

  // Investment-scoped capabilities (require investmentId in context)
  if (capability.startsWith('investment.')) {
    if (!context?.investmentId) return false;
    return evaluateInvestmentCapability(user, capability, context);
  }

  // Milestone-scoped capabilities (require milestoneId in context)
  if (capability.startsWith('milestone.')) {
    if (!context?.milestoneId) return false;
    return evaluateMilestoneCapability(user, capability, context);
  }

  // Document-scoped capabilities: require at least one scope anchor
  // (documentId for explicit-grant checks, OR projectId for project-relationship
  // based access, OR vaultId for vault-membership access). Fail closed when none.
  if (capability.startsWith('document.')) {
    if (!context?.documentId && !context?.projectId && !context?.vaultId) return false;
    return evaluateDocumentCapability(user, capability, context);
  }

  // Vault-scoped capabilities
  if (capability.startsWith('vault.')) {
    if (!context?.vaultId && !context?.projectId) return false;
    return evaluateVaultCapability(user, capability, context);
  }

  // Folder-scoped capabilities (project-scoped)
  if (capability.startsWith('folder.')) {
    if (!context?.projectId) return false; // Fail closed: no project scope
    return evaluateFolderCapability(user, capability, context);
  }

  // Task-scoped capabilities (require projectId for the task's parent project)
  if (capability.startsWith('task.')) {
    if (!context?.projectId) return false; // Fail closed: no project scope
    return evaluateTaskCapability(user, capability, context);
  }

  // User management (organization admin or platform admin only)
  if (capability === 'users.manage') {
    return user.platformRole === 'platform_admin' || isOrganizationAdminRole(user.accountRole);
  }

  return false;
}

/**
 * Project capability evaluation based on account role + project relationship
 */
function evaluateProjectCapability(user: UserAuthState, capability: Capability, context: ScopedContext): boolean {
  const { projectId } = context;
  // Legacy single-value relationship compatibility (deprecated shape).
  const legacyProjectManager = !!(projectId && (
    user.relationships?.['project_manager'] === projectId ||
    user.relationships?.['project_lead'] === projectId
  ));
  const legacyProjectMember = !!(projectId && (
    legacyProjectManager || user.relationships?.['project_member'] === projectId
  ));
  // Canonical scoped-relationship facts (fed from scopedRelationshipResolver).
  const isProjectManager = legacyProjectManager
    || hasRelationshipForResource(user, 'project', projectId, ['project_manager']);
  const hasAnyProjectRelationship = legacyProjectMember
    || hasRelationshipForResource(user, 'project', projectId);
  const isOrgAdmin = isOrganizationAdminRole(user.accountRole);

  // Account-level authorization for project operations
  switch (capability) {
    case 'project.view':
      // Organization admin or any active project relationship
      return isOrgAdmin || hasAnyProjectRelationship;
    case 'project.edit':
      // Project manager or organization admin
      return isOrgAdmin || isProjectManager;
    case 'project.manage':
      // Project manager or organization admin
      return isOrgAdmin || isProjectManager;
    case 'project.participant.view':
      // Organization admin or project member
      return isOrgAdmin || hasAnyProjectRelationship;
    case 'project.participant.manage':
      // Project manager or organization admin
      return isOrgAdmin || isProjectManager;
    case 'project.delete':
      // Organization admin only
      return isOrgAdmin;
    default:
      return false;
  }
}

/**
 * Workspace capability evaluation based on account role
 */
function evaluateWorkspaceCapability(user: UserAuthState, capability: Capability, context: ScopedContext): boolean {
  const isOrgAdmin = isOrganizationAdminRole(user.accountRole);
  const isOrgMember = user.accountRole === 'organization_member';
  const hasWorkspaceRelationship = hasRelationshipForResource(user, 'workspace', context.workspaceId);
  // Workspace operations require canonical organization administration.
  switch (capability) {
    case 'workspace.view':
      // Organization admin/member (via org membership) or workspace relationship
      return isOrgAdmin || isOrgMember || hasWorkspaceRelationship;
    case 'workspace.manage':
    case 'workspace.members.manage':
      // Organization admin only
      return isOrgAdmin;
    case 'workspace.members.invite':
      // Organization admin (member invitation authority is handled by the
      // invitation authority layer, not workspace membership alone)
      return isOrgAdmin;
    // Stage-1 SURFACE discovery: organization admin OR an active workspace_member
    // relationship. organization_member ALONE grants nothing. The visible rows
    // are then filtered by resource-scoped canResolve (Stage 2).
    case 'workspace.projects.list':
    case 'workspace.tasks.list':
    case 'workspace.files.list':
      return isOrgAdmin || hasWorkspaceRelationship;
    // Analytics/reporting requires organization administration (or a separate
    // reporting entitlement resolved elsewhere). Plain workspace membership is
    // insufficient.
    case 'workspace.analytics.view':
      return isOrgAdmin;
    default:
      return false;
  }
}

/**
 * Investment capability evaluation based on investment relationship
 */
function evaluateInvestmentCapability(user: UserAuthState, capability: Capability, context: ScopedContext): boolean {
  const { investmentId } = context;
  const isOrgAdmin = isOrganizationAdminRole(user.accountRole);
  const isInvestorRep = hasRelationshipForResource(user, 'investment', investmentId, [
    'investor_representative', 'investment_manager',
  ]) || user.relationships?.['investor'] === investmentId
    || user.relationships?.['investment_viewer'] === investmentId;
  const isInvestmentManager = hasRelationshipForResource(user, 'investment', investmentId, [
    'investment_manager',
  ]);

  switch (capability) {
    case 'investment.view':
      // Organization admin or investment participant
      return isOrgAdmin || isInvestorRep;
    case 'investment.approve':
      // Investment manager or organization admin
      return isOrgAdmin || isInvestmentManager
        || user.relationships?.['investment_approver'] === investmentId;
    case 'investment.manage':
      // Organization admin only
      return isOrgAdmin;
    default:
      return false;
  }
}

/**
 * Milestone capability evaluation based on milestone relationship
 */
function evaluateMilestoneCapability(user: UserAuthState, capability: Capability, context: ScopedContext): boolean {
  const { milestoneId } = context;
  const isOrgAdmin = isOrganizationAdminRole(user.accountRole);

  switch (capability) {
    case 'milestone.view':
      // Organization admin/member or milestone reviewer relationship
      return isOrgAdmin || user.accountRole === 'organization_member'
        || hasRelationshipForResource(user, 'project', context.projectId);
    case 'milestone.edit':
    case 'milestone.gate.approve':
      // Organization admin or milestone reviewer
      return isOrgAdmin ||
             user.relationships?.['milestone_reviewer'] === milestoneId;
    default:
      return false;
  }
}

/**
 * Document capability evaluation.
 *
 * Documents are primarily scoped through their owning project (projectId).
 * Explicit grants (documentId) and vault membership (vaultId) are secondary
 * access paths. Fail closed when no scope anchor is present.
 */
function evaluateDocumentCapability(user: UserAuthState, capability: Capability, context: ScopedContext): boolean {
  const { documentId, projectId, vaultId } = context;
  const isOrgAdmin = isOrganizationAdminRole(user.accountRole);

  // Project-scoped relationships
  const legacyPM = !!(projectId && (
    user.relationships?.['project_manager'] === projectId ||
    user.relationships?.['project_lead'] === projectId
  ));
  const isProjectManager = legacyPM
    || hasRelationshipForResource(user, 'project', projectId, ['project_manager']);
  const hasAnyProjectRelationship = legacyPM
    || hasRelationshipForResource(user, 'project', projectId);

  // Vault membership
  const hasVaultAccess = vaultId
    ? hasRelationshipForResource(user, 'vault', vaultId)
    : false;

  // Own-document self-service (uploader/creator)
  const isOwnDocument = !!(context.documentOwnerId && context.documentOwnerId === user.userId);

  // Legacy single-doc explicit grants
  const hasExplicitViewGrant = user.relationships?.['document_viewer'] === documentId;

  switch (capability) {
    case 'document.view':
    case 'document.preview':
      return isOrgAdmin || hasAnyProjectRelationship || hasVaultAccess || hasExplicitViewGrant || isOwnDocument;

    case 'document.download':
      // Separate from view; same base authority set for regular download.
      return isOrgAdmin || hasAnyProjectRelationship || hasVaultAccess || hasExplicitViewGrant || isOwnDocument;

    case 'document.restricted_download':
      // Stricter than ordinary download: org admin or an explicit restricted-download grant.
      // Project/vault membership alone is insufficient.
      return isOrgAdmin || user.relationships?.['document_restricted_downloader'] === documentId;

    case 'document.upload':
    case 'document.update':
      return isOrgAdmin || isProjectManager || isOwnDocument;

    case 'document.delete':
      return isOrgAdmin || isProjectManager || isOwnDocument;

    case 'document.approve':
      // Approval requires org admin or an explicit `validator` project relationship.
      // Project_manager alone is insufficient (mirrors task.approve).
      return isOrgAdmin
        || (projectId ? hasRelationshipForResource(user, 'project', projectId, ['validator']) : false);

    case 'document.sign':
      return isOrgAdmin || user.relationships?.['document_signatory'] === documentId;

    case 'document.share':
      return isOrgAdmin || isProjectManager;

    case 'document.internal_details.view':
      // Internal comments, contributor lists, security scan metadata: PM or org admin.
      return isOrgAdmin || isProjectManager;

    default:
      return false;
  }
}

/**
 * Vault / data-room capability evaluation.
 *
 * Vault access is independent from ordinary document access — holding a
 * project relationship grants vault.view only when the vault is linked to
 * that project; vault.manage and link/unlink require explicit vault authority.
 */
function evaluateVaultCapability(user: UserAuthState, capability: Capability, context: ScopedContext): boolean {
  const { vaultId, projectId } = context;
  const isOrgAdmin = isOrganizationAdminRole(user.accountRole);
  const hasVaultMembership = vaultId ? hasRelationshipForResource(user, 'vault', vaultId) : false;
  const hasVaultEditAccess = vaultId
    ? hasRelationshipForResource(user, 'vault', vaultId, ['vault_editor', 'vault_manager'])
    : false;
  const hasProjectRelationship = projectId
    ? hasRelationshipForResource(user, 'project', projectId)
    : false;

  switch (capability) {
    case 'vault.view':
      // Vault member, project relationship on the vault's linked project, or org admin.
      return isOrgAdmin || hasVaultMembership || hasProjectRelationship;
    case 'vault.contents.manage':
      return !!vaultId && !!projectId && (isOrgAdmin
        || hasRelationshipForResource(user, 'project', projectId, ['project_manager']));
    case 'vault.security.manage':
      // Project assignment never confers room security/disclosure administration.
      return !!vaultId && !!projectId && isOrgAdmin;
    case 'vault.manage':
      return isOrgAdmin || hasRelationshipForResource(user, 'vault', vaultId, ['vault_manager']);
    case 'vault.documents.link':
    case 'vault.documents.unlink':
      return isOrgAdmin || hasVaultEditAccess;
    default:
      return false;
  }
}

/**
 * Folder capability evaluation (project-scoped).
 */
function evaluateFolderCapability(user: UserAuthState, capability: Capability, context: ScopedContext): boolean {
  const { projectId } = context;
  const isOrgAdmin = isOrganizationAdminRole(user.accountRole);
  const legacyPM = !!(projectId && (
    user.relationships?.['project_manager'] === projectId ||
    user.relationships?.['project_lead'] === projectId
  ));
  const isProjectManager = legacyPM
    || hasRelationshipForResource(user, 'project', projectId, ['project_manager']);

  switch (capability) {
    case 'folder.create':
    case 'folder.manage':
      return isOrgAdmin || isProjectManager;
    default:
      return false;
  }
}

/**
 * Task capability evaluation. Tasks are always scoped to a parent project;
 * `context.projectId` is required (caller guarantees this). Task assignment
 * permits narrow own-task self-service only and NEVER grants project-management
 * authority (task.assign / task.delete / task.participants.manage).
 *
 * Approval requires a `validator` project relationship or an explicit right;
 * project_manager authority alone is insufficient for capital-gate approval.
 */
function evaluateTaskCapability(
  user: UserAuthState,
  capability: Capability,
  context: ScopedContext,
): boolean {
  const { projectId } = context;
  const isOrgAdmin = isOrganizationAdminRole(user.accountRole);

  // Legacy single-value shape compatibility
  const legacyProjectManager = !!(
    user.relationships?.['project_manager'] === projectId ||
    user.relationships?.['project_lead'] === projectId
  );
  const isProjectManager =
    legacyProjectManager ||
    hasRelationshipForResource(user, 'project', projectId, ['project_manager']);
  const hasAnyProjectRelationship =
    legacyProjectManager ||
    hasRelationshipForResource(user, 'project', projectId);
  const hasExactTaskValidator = !!context.taskId
    && hasRelationshipForResource(user, 'task', context.taskId, ['validator']);

  // Own-task self-service: the acting user is the assignee or creator.
  // Self-service NEVER implies project-management authority.
  const isOwnTask =
    (!!context.taskAssigneeId && context.taskAssigneeId === user.userId) ||
    (!!context.taskCreatorId && context.taskCreatorId === user.userId);

  switch (capability) {
    case 'task.view':
    case 'task.comment':
    case 'task.attachment.view':
    case 'task.attachment.download':
      // Project relationship, exact-object review assignment, or own-task
      // self-service. Exact-object review never grants parent Project access.
      return isOrgAdmin || hasAnyProjectRelationship || hasExactTaskValidator || isOwnTask;

    case 'task.update':
    case 'task.status.update':
      // Project manager, org admin, OR own-task (assignee/creator may update
      // their own task — e.g. marking progress, submitting work)
      return isOrgAdmin || isProjectManager || isOwnTask;

    case 'task.create':
    case 'task.participants.manage':
      // Project management authority required; self-service never grants creation
      return isOrgAdmin || isProjectManager;

    case 'task.assign':
      // Assignment is a project-management action; assignment never grants itself
      return isOrgAdmin || isProjectManager;

    case 'task.delete':
      // Project manager or org admin; creator may delete their own task (own-task
      // only uses taskCreatorId, NOT taskAssigneeId — being assigned ≠ ownership)
      return (
        isOrgAdmin ||
        isProjectManager ||
        !!(context.taskCreatorId && context.taskCreatorId === user.userId)
      );

    case 'task.approve':
      // Capital-gate / milestone approval is separated from routine manage
      // access. Requires org admin OR an explicit `validator` relationship on the
      // task's project or the exact Task (not project_manager alone). Explicit
      // rights are checked before this function is called (see canResolve).
      return (
        isOrgAdmin ||
        hasRelationshipForResource(user, 'project', projectId, ['validator']) ||
        hasExactTaskValidator
      );

    case 'task.internal_details.view':
      // Internal notes, audit history, assignee metadata beyond client-safe
      // projection: project manager or org admin only
      return isOrgAdmin || isProjectManager;

    default:
      return false;
  }
}
